import { X } from "lucide-react";
import { useLayoutEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { Button } from "./Button";
import { useDesignSystem } from "./DesignSystem";
import { dialogFocusTargets, isTopModal, useModalLayer } from "./useModalLayer";
import "./overlays.css";

type ModalProps = {
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  busy?: boolean;
  eyebrow?: ReactNode;
  description?: ReactNode;
  closeLabel?: string;
  className?: string;
  bodyClassName?: string;
  testId?: string;
  side?: boolean;
  /** 한 걸음 뒤로. 주면 제목 왼쪽에 ‹ 가 서고, 오른쪽 ✕ 는 화면을 아예 떠나는 문이 된다. */
  onBack?: () => void;
  /** 손잡이 옆에 세울 때 쓰는 자리. 주지 않으면 화면 가운데다. */
  style?: CSSProperties;
  restoreFocus?: () => HTMLElement | null;
  onKeyDown?: (event: KeyboardEvent<HTMLDialogElement>) => void;
};

/** Keep the DOM in its page scope; the native top layer supplies background inertness. */
export function Modal({ title, children, onClose, busy = false, eyebrow, description, closeLabel = "닫기", className = "", bodyClassName = "", testId, side = false, onBack, style, restoreFocus, onKeyDown }: ModalProps) {
  const id = useId();
  const themed = useDesignSystem();
  const ref = useRef<HTMLDialogElement>(null);
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy);
  const backdropDown = useRef(false);
  const heading = useRef<HTMLDivElement>(null);
  const [headingScrollable, setHeadingScrollable] = useState(false);
  closeRef.current = onClose;
  busyRef.current = busy;
  const requestClose = () => { if (isTopModal(ref.current) && !busyRef.current) closeRef.current(); };
  /* 클래스로 받는다 — 이 창을 쓰는 곳이 이미 className으로 제 모양을 말하고 있어, 같은 것을
     prop으로 한 번 더 받으면 두 곳에서 정하는 값이 된다. */
  const bare = className.includes("ui-dialog--bare");
  useModalLayer(ref, restoreFocus);
  useLayoutEffect(() => {
    // A mutation must not lose its visible result through route/history changes.
    // This also covers a busy lower layer when another modal is on top.
    const leave = (event: Event) => { if (busyRef.current) event.preventDefault(); };
    const unload = (event: BeforeUnloadEvent) => {
      if (!busyRef.current) return;
      event.preventDefault(); event.returnValue = "";
    };
    window.addEventListener("planner-settings-leave", leave);
    window.addEventListener("beforeunload", unload);
    return () => {
      window.removeEventListener("planner-settings-leave", leave);
      window.removeEventListener("beforeunload", unload);
    };
  }, []);
  useLayoutEffect(() => {
    // 머리줄이 없는 창(팝오버)에는 잴 것이 없다. 이 자리를 지키지 않았을 때는 ResizeObserver가
    // null을 받고 그 자리에서 터졌고, 터진 곳이 layout effect라 화면 전체가 떨어져 나가
    // 사이트가 통째로 새로 뜬 것처럼 보였다.
    const node = heading.current;
    if (!node) { setHeadingScrollable(false); return; }
    // Korean glyph ink can exceed a 25px line box by 2px without needing a
    // scroller. Only a heading constrained by its actual max-height is scrollable.
    const measure = () => setHeadingScrollable(node.clientHeight >= parseFloat(getComputedStyle(node).maxHeight) - 1 && node.scrollHeight > node.clientHeight + 1);
    const observer = new ResizeObserver(measure); observer.observe(node); measure();
    return () => observer.disconnect();
  }, [title, description, eyebrow, bare]);

  const keydown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (!isTopModal(ref.current)) return;
    onKeyDown?.(event);
    if (event.defaultPrevented || event.key !== "Tab") return;
    const node = ref.current!;
    const items = dialogFocusTargets(node);
    const first = items[0], last = items.at(-1);
    if (!first) { event.preventDefault(); node.focus(); return; }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || document.activeElement === node)) { event.preventDefault(); first.focus(); }
  };
  const outside = (x: number, y: number) => {
    const rect = ref.current!.getBoundingClientRect();
    return x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;
  };

  return <dialog ref={ref} style={style} className={`ui-dialog ${themed ? "ui-next" : ""} ${side ? "ui-dialog--side" : ""} ${className}`.trim()} tabIndex={-1}
    aria-modal="true" aria-label={bare ? String(title) : undefined} aria-labelledby={bare ? undefined : `${id}-title`} aria-describedby={description ? `${id}-description` : undefined}
    aria-busy={busy || undefined} data-testid={testId} onKeyDown={keydown}
    onCancel={event => { event.preventDefault(); event.stopPropagation(); requestClose(); }}
    onPointerDown={event => { backdropDown.current = event.target === event.currentTarget && outside(event.clientX, event.clientY); }}
    onPointerCancel={() => { backdropDown.current = false; }}
    onClick={event => { if (backdropDown.current && event.target === event.currentTarget && outside(event.clientX, event.clientY)) requestClose(); backdropDown.current = false; }}>
    {/* 제목도 손잡이도 없는 창(팝오버)은 머리줄을 세우지 않는다 — 빈 줄이 값 위에 자리만
        차지한다. 닫는 길은 바깥 누르기와 Esc가 맡는다. */}
    {!bare && <header className="ui-dialog-header">
      {/* 돌아가기는 제목 왼쪽, 떠나기는 오른쪽 — 둘이 하는 일이 다르므로 자리도 다르다.
          한 걸음 안으로 들어왔어도 화면을 통째로 떠나고 싶을 수 있다. */}
      {onBack && <Button className="ui-dialog-back" variant="quiet" size="icon" aria-label="뒤로" disabled={busy} onClick={onBack}>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 8 12l7 7" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </Button>}
      <div ref={heading} tabIndex={headingScrollable ? 0 : -1} data-scrollable={headingScrollable || undefined} role={headingScrollable ? "region" : undefined} aria-label={headingScrollable ? "창 제목" : undefined}>{eyebrow && <span className="ui-dialog-eyebrow">{eyebrow}</span>}<h2 id={`${id}-title`}>{title}</h2>{description && <p id={`${id}-description`}>{description}</p>}</div>
      <Button variant="quiet" size="icon" aria-label={closeLabel} disabled={busy} onClick={requestClose}>
        {/* 설정 창 ✕ 와 같은 그림(lucide X 20) — 창을 오갈 때 ✕ 가 흔들리지 않게 한 벌만 쓴다(2026-10-19 밤 지윤). */}
        <X size={20} aria-hidden="true" />
      </Button>
    </header>}
    <div className={`ui-dialog-body ${bodyClassName}`.trim()}>{children}</div>
  </dialog>;
}

/** A modal side panel: overlays the workspace without changing its measured bounds. */
export function Drawer(props: Omit<ModalProps, "side">) {
  return <Modal {...props} side />;
}
